import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useBuildingPermit } from 'hooks/useBuildingPermit';
import { useBuildingPermitV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { City } from 'utils/constants/location_legacy';
import { parseRelatedContactsIntoList } from 'utils/contacts';
import { formatProjectCost } from 'utils/currency';
import { isCityMatch } from 'utils/misc';

export const BuildingPermit = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result:
    | ReturnType<typeof useBuildingPermitV4>
    | ReturnType<typeof useBuildingPermit>;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useBuildingPermitV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('buildingPermit');
    resolvedDocumentId = documentUuid || undefined;
    result = useBuildingPermit(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  const permitUrl = result.data?.document_url;
  const currentCity = result.data?.city;
  const isDallas = isCityMatch(currentCity, City.DALLAS);

  let workTypeContent;
  if (isDallas) {
    workTypeContent = result.data?.metadata.workclass ? (
      <Text textTransform="capitalize">{result.data?.metadata.workclass}</Text>
    ) : (
      <EmptyField />
    );
  } else {
    const combinedText = [
      result.data?.metadata.workclass,
      result.data?.metadata.permitclassmapped,
      result.data?.metadata.workclassgroup,
    ]
      .filter(Boolean)
      .join(', ');
    workTypeContent = <Text textTransform="capitalize">{combinedText}</Text>;
  }

  let proposedUseContent;
  if (isDallas) {
    proposedUseContent = result.data?.metadata.proposedusedescription ? (
      <Text>{result.data?.metadata.proposedusedescription}</Text>
    ) : (
      <EmptyField />
    );
  } else {
    proposedUseContent = result.data?.metadata.permitclass ? (
      <Text>{result.data?.metadata.permitclass}</Text>
    ) : (
      <EmptyField />
    );
  }

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Building Permit">
          {result.data?.metadata?.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={
            (result.data?.metadata as { parcel_address?: string | null })
              ?.parcel_address ??
            result.data?.metadata?.originaladdress ??
            ''
          }
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        {/* Companies */}
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        {/* Project details */}
        <InfoModal.Row>
          <Label>Project Name</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.projectname ? (
                <Text fontWeight="bold">{result.data.metadata.projectname}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Description</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata.description ||
              result.data?.metadata.proposedusedescription ? (
                <Text>
                  {result.data?.metadata.description}
                  {result.data?.metadata.proposedusedescription
                    ? ` ${result.data?.metadata.proposedusedescription}`
                    : ''}
                </Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Proposed Use</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {proposedUseContent}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Work Type</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {workTypeContent}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        <InfoModal.Row hideDivider>
          <Label>Project Value</Label>
          <InfoModal.RowDetails>
            <Skeleton variant="text" noOfLines={1}>
              {result.data?.metadata?.estprojectcost ? (
                <Text>{formatProjectCost(result.data?.metadata?.estprojectcost)}</Text>
              ) : (
                <EmptyField />
              )}
            </Skeleton>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {/* Status */}
        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        {/* Links */}
        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              {permitUrl ? (
                <ExternalLink
                  to={permitUrl}
                  onClick={() => trackProjectMaterials(permitUrl)}
                >
                  View application
                </ExternalLink>
              ) : (
                <EmptyField />
              )}
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
